import React from 'react';
import { useState, useEffect } from 'react';
import { Heart, Target, Eye, Users, Award, Shield, Building } from 'lucide-react';
import { getGallery, GalleryItem } from '../utils/storage';

const About: React.FC = () => {
  const [, setGalleryItems] = useState<GalleryItem[]>([]);
  const [, setLoading] = useState(true);
  const [] = useState<string>('all');

  useEffect(() => {
    loadGallery();
    
    // Refresh gallery every 30 seconds to show new uploads
    const interval = setInterval(loadGallery, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadGallery = async () => {
    try {
      console.log('🖼️ Loading gallery images...');
      const items = await getGallery();
      console.log('🖼️ Gallery items loaded:', items.length);
      setGalleryItems(items);
    } catch (error) {
      console.error('Error loading gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories
  
  // Filter items by category

  const values = [
    {
      icon: Heart,
      title: 'Integrity',
      description: 'We act in a fair, trustworthy manner following professional and ethical standards to deliver high quality services.'
    },
    {
      icon: Shield,
      title: 'Collaboration',
      description: 'We work in cooperation as a Team.'
    },
    {
      icon: Award,
      title: 'Commitment',
      description: 'We are dedicated in excellence through our personal effort and those the Team.'
    },
    {
      icon: Target,
      title: 'Community',
      description: 'We recognize and respect our community\'s health.'
    },
    {
      icon: Users,
      title: 'Innovation',
      description: 'We are dynamic in health-care provision through new technologies and timely access to information.'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white section-padding">
        <div className="container-max">
          <div className="text-center">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">About Rabininsia Memorial Hospital</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Learn about our commitment to providing exceptional healthcare services 
              to the community of Dar es Salaam and beyond.
            </p>
          </div>
        </div>
      </section>

      {/* Executive Director's Note */}
      <section className="section-padding">
        <div className="container-max">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Welcome to Rabininsia Memorial Hospital</h2>
            <blockquote className="text-xl text-gray-700 italic mb-6 border-l-4 border-blue-600 pl-6">
              "At Rabininsia, we don't just treat illness — we care for lives with dignity and integrity."
            </blockquote>
            <p className="text-gray-600 leading-relaxed">
              Welcome to Rabininsia Memorial Hospital, where our commitment to excellence in healthcare 
              drives everything we do. Our dedicated team of medical professionals works tirelessly to 
              ensure that every patient receives the highest quality care in a compassionate environment.
            </p>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="bg-gray-50 section-padding">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {/* Mission */}
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Target className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-gray-600">
                To Provide and enhance accessible, comprehensive health-care services to our community that are: Quality-driven, Customer-focused and Cost-effective, i.e. "Home of Hospitality".
              </p>
            </div>

            {/* Vision */}
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Eye className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-gray-600">
                Rabininsia Memorial Hospital to be a Center of Excellence for Super-specialized service of high quality and affordable in East Africa and the World in general.
              </p>
            </div>

            {/* Motto */}
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Motto</h3>
              <p className="text-gray-600">
                We Listen, we Care, we Cure
              </p>
            </div>
          </div>

          {/* Core Values */}
          <div>
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Core Values</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <div key={index} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
                    <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Hospital Stats */}
      <section className="section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Impact</h2>
            <p className="text-xl text-gray-600">Numbers that reflect our commitment to the community</p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">15+</div>
              <div className="text-gray-600">Years of Service</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">50+</div>
              <div className="text-gray-600">Medical Specialists</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">10,000+</div>
              <div className="text-gray-600">Patients Served</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">24/7</div>
              <div className="text-gray-600">Emergency Care</div>
            </div>
          </div>
        </div>
      </section>

      {/* Career Section */}
      <section className="section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Career Opportunities</h2>
            <p className="text-xl text-gray-600">Join our team of dedicated healthcare professionals</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Medical Professionals</h3>
              <p className="text-gray-600 mb-4">We're always looking for qualified doctors, nurses, and specialists to join our team.</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Competitive salary packages</li>
                <li>• Professional development opportunities</li>
                <li>• Modern medical facilities</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <Building className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Administrative Staff</h3>
              <p className="text-gray-600 mb-4">Support roles in administration, finance, HR, and patient services.</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Growth opportunities</li>
                <li>• Training programs</li>
                <li>• Team-oriented environment</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <Heart className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Support Services</h3>
              <p className="text-gray-600 mb-4">Technical, maintenance, security, and other essential support roles.</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Stable employment</li>
                <li>• Skills development</li>
                <li>• Healthcare benefits</li>
              </ul>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 max-w-2xl mx-auto">
              <h3 className="text-xl font-bold text-blue-900 mb-4">How to Apply</h3>
              <p className="text-blue-800 mb-4">
                Send your CV and cover letter to our HR department
              </p>
              <div className="space-y-2">
                <p className="text-blue-700"><strong>Email:</strong> hr@rabininsiamemorial.co.tz</p>
                <p className="text-blue-700"><strong>Phone:</strong> +255 627 499 419</p>
                <p className="text-blue-700"><strong>Address:</strong> HR Department, Rabininsia Memorial Hospital, Namanga-Tegeta, Dar es Salaam</p>
              </div>
            </div>
          </div>
        </div>
      </section>
{/* Gallery Section */}
<section className="bg-gray-50 section-padding">
  <div className="container-max">
    <div className="text-center mb-12">
      <h2 className="text-3xl font-bold text-gray-900 mb-4">Hospital Gallery</h2>
      <p className="text-xl text-gray-600">Explore our modern facilities and services</p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {[
        // Hospital Facilities
        '/WEBSITE/Hospital Facilities/facility-1.JPG',
        '/WEBSITE/Hospital Facilities/icu.jpg',
        '/WEBSITE/Hospital Facilities/ambulannce1.jpg',
        // Medical Equipment
        '/WEBSITE/Medical Equipment/X-RAY.jpg',
        '/WEBSITE/Medical Equipment/ultrasaound.jpeg',
        '/WEBSITE/Medical Equipment/VENTILATOR.jpeg',
        // Staff & Team
        '/WEBSITE/Staff & Team/staff-1.JPG',
        '/WEBSITE/Staff & Team/staff-2.JPG',
        '/WEBSITE/Staff & Team/staff-13.JPG',
        // Events & Activities
        '/WEBSITE/Events & Activities/event-1.jpg',
        '/WEBSITE/Events & Activities/event-2.JPG',
        '/WEBSITE/Events & Activities/event-3.JPG',
        // Patient Care
        '/WEBSITE/Patient Care/patient-1.JPG',
        '/WEBSITE/Patient Care/patient-2.JPG',
        '/WEBSITE/Patient Care/patient-3.JPG',
      ].map((src, index) => (
        <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="h-48 overflow-hidden">
            <img
              src={src}
              alt={`Gallery Image ${index + 1}`}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src =
                  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y3ZjdmNyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
              }}
            />
          </div>
        </div>
      ))}
    </div>
  </div>
</section>


      {/* History Timeline */}
      <section className="section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Journey</h2>
            <p className="text-xl text-gray-600">A timeline of our growth and achievements</p>
          </div>
          
          <div className="space-y-8">
            <div className="flex items-center space-x-6">
              <div className="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold">
                2010
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900">Hospital Foundation</h3>
                <p className="text-gray-600">Rabininsia Memorial Hospital was established with a vision to serve the community.</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-6">
              <div className="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold">
                2015
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900">Expansion of Services</h3>
                <p className="text-gray-600">Added specialized departments including cardiology and neurology.</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-6">
              <div className="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold">
                2020
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900">Modern Equipment</h3>
                <p className="text-gray-600">Invested in state-of-the-art medical equipment and technology.</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-6">
              <div className="bg-green-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold">
                2025
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900">Digital Transformation</h3>
                <p className="text-gray-600">Launched online appointment system and digital health records.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;